import { useState, useEffect, useRef } from 'react';
import { ChevronRight, FileText, CheckCircle2, Zap, Loader2 } from 'lucide-react';
import { httpsCallable } from '../lib/callable';
import { functions } from '../firebase';
import { onSnapshot } from 'firebase/firestore';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { artifactDoc, projectDoc } from '../constants/firestorePaths';
import { enqueueSave } from '../features/crf/saveConcurrency';
import { docChangedMessage, isRefusedAsChanged } from '../utils/versionedDoc';
import { useToast } from '../contexts/ToastContext';
import { consentSectionOrder, privacyNoticeNeeded, PRIVACY_NOTICE_TEXT, REDRAFT_CONFIRM_TEXT } from '../features/consent/consentSectionOrder';
import {
    afterPreviousSectionSave,
    approveAllDrafts,
    approveAllQuestion,
    approveAllSummary,
    buildAllPlan,
    buildAllQuestion,
    buildAllSummary,
    serverFailureReason,
    consentSyncActions,
    decideConsentLeaveSave,
    navigationAllowedAfterFlush,
    regenerateNeedsConfirm,
    reopenedNoticeNeeded,
    LEAVE_BLOCKED_ERROR_TEXT,
    LEAVE_BLOCKED_REFUSED_TEXT,
    MOVED_AWAY_UNSAVED_TEXT,
    REOPENED_NOTICE_TEXT,
    REGENERATE_APPROVED_CONFIRM_TEXT,
    type ApprovableDraft,
    type BuildAllStep,
    type FlushOutcome,
} from '../features/consent/consentReapproval';

interface ArtifactBuilderProps {
    projectId: string;
    artifactId: string;
    onClose: () => void;
    /**
     * Set the consent form up again for the study's current countries. When
     * given, the page offers it next to the notice that the countries changed.
     */
    onRedraft?: () => void;
}

export default function ArtifactBuilder({ projectId, artifactId, onClose, onRedraft }: ArtifactBuilderProps) {
    const [builderState, setBuilderState] = useState<any>(null);
    // The study's countries now, to tell whether they changed after setup.
    const [studyCountries, setStudyCountries] = useState<unknown>(undefined);
    const [currentContent, setCurrentContent] = useState('');
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

    const [artifactType, setArtifactType] = useState<string>('consent');
    const [buildingAll, setBuildingAll] = useState(false);
    const [buildAllProgress, setBuildAllProgress] = useState({ current: 0, total: 0, currentSection: '' });
    const buildAllCancelledRef = useRef(false);

    // Optimistic concurrency for section drafts (same shape as the CRF
    // Builder's form saves). Per section: the version of the text this page
    // took on, saves queued one at a time, and sections refused as stale.
    const draftVersionsRef = useRef(new Map<string, number>());
    const draftQueueRef = useRef(new Map<string, Promise<unknown>>());
    const staleSectionsRef = useRef(new Set<string>());
    // Sections whose text the user typed into since it was last saved or
    // approved. Leaving a section saves it only when it is in here, so an
    // approved section nobody touched is never saved and never reopened.
    const userChangedRef = useRef(new Set<string>());
    // The latest text and builder state, for saves that run after a render
    // (leaving the page, closing the builder).
    const contentRef = useRef('');
    const builderStateRef = useRef<any>(null);

    // Per-section generation progress
    const [genProgress, setGenProgress] = useState({ step: 0, message: '', percent: 0 });
    contentRef.current = currentContent;
    builderStateRef.current = builderState;
    const genTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const startGenProgress = (sectionTitle: string) => {
        const steps = [
            `Loading protocol context...`,
            `Applying regulatory guidelines...`,
            `Writing ${sectionTitle} content...`,
            `Reviewing compliance requirements...`,
            `Finalizing section...`,
        ];
        let idx = 0;
        setGenProgress({ step: 0, message: steps[0], percent: 10 });
        if (genTimerRef.current) clearInterval(genTimerRef.current);
        genTimerRef.current = setInterval(() => {
            idx++;
            if (idx < steps.length) {
                setGenProgress({ step: idx, message: steps[idx], percent: Math.min(90, 10 + idx * 20) });
            }
        }, 6000);
    };

    const stopGenProgress = () => {
        if (genTimerRef.current) { clearInterval(genTimerRef.current); genTimerRef.current = null; }
        setGenProgress({ step: 0, message: '', percent: 0 });
    };

    // The consent form's sections come from the builder state: the server
    // stores the study's own list, whose privacy sections follow the study's
    // countries. Older builder states keep the list they were set up from.
    const CONSENT_SECTION_ORDER = consentSectionOrder(builderState);

    const CRF_SECTION_ORDER = [
        { id: "header", title: "1. Header & Visit Info" },
        { id: "ie", title: "2. Eligibility (IE)" },
        { id: "demographics", title: "3. Demographics (DM)" },
        { id: "history", title: "4. Medical History (MH)" },
        { id: "vitals", title: "5. Vital Signs (VS)" },
        { id: "ae_cm", title: "6. Safety (AE & CM)" }
    ];

    const currentSectionOrder = artifactType === 'case-report-form' ? CRF_SECTION_ORDER : CONSENT_SECTION_ORDER;

    useEffect(() => {
        const unsub = onSnapshot(artifactDoc(projectId, artifactId), (snapshot) => {
            const data = snapshot.data();
            if (data) {
                if (data.type) setArtifactType(data.type);
                if (data.builderState) {
                    setBuilderState(data.builderState);
                }
            }
        });
        return () => { unsub(); stopGenProgress(); };
    }, [projectId, artifactId]);

    useEffect(() => {
        const unsub = onSnapshot(projectDoc(projectId), (snapshot) => {
            setStudyCountries(snapshot.data()?.studySetup?.countries);
        });
        return () => unsub();
    }, [projectId]);

    // A redraft starts every section again, so the page asks first.
    const [confirmingRedraft, setConfirmingRedraft] = useState(false);
    const showPrivacyNotice = artifactType !== 'case-report-form' && privacyNoticeNeeded(builderState, studyCountries);
    const privacyNotice = showPrivacyNotice ? (
        <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="flex items-center justify-between gap-4">
                <span>{PRIVACY_NOTICE_TEXT}</span>
                {onRedraft && !confirmingRedraft && (
                    <button
                        onClick={() => setConfirmingRedraft(true)}
                        className="shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-medium text-amber-900 hover:bg-amber-100"
                    >
                        Redraft
                    </button>
                )}
            </div>
            {onRedraft && confirmingRedraft && (
                <div className="mt-3 flex items-center justify-between gap-4 border-t border-amber-200 pt-3">
                    <span>{REDRAFT_CONFIRM_TEXT}</span>
                    <div className="flex shrink-0 gap-2">
                        <button
                            onClick={() => setConfirmingRedraft(false)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => { setConfirmingRedraft(false); onRedraft(); }}
                            className="rounded-lg bg-amber-600 px-3 py-1.5 font-medium text-white hover:bg-amber-700"
                        >
                            Redraft
                        </button>
                    </div>
                </div>
            )}
        </div>
    ) : null;

    const currentSectionId = builderState ? currentSectionOrder[builderState.currentSectionIndex]?.id : null;

    // Regenerating an approved section replaces its text and sets it back to
    // draft, so the page asks first. Moving to another section drops the ask.
    const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
    useEffect(() => { setConfirmingRegenerate(false); }, [currentSectionId]);

    // Sync local editor when remote state changes. consentSyncActions makes
    // the whole decision (keep unsaved typing, never move the base version
    // under it, save text left in a section the form moved away from); this
    // effect only carries it out.
    const lastSyncedSectionRef = useRef<string | null>(null);
    useEffect(() => {
        if (builderState && currentSectionId) {
            const sectionData = builderState.sections[currentSectionId];
            if (sectionData) {
                const sync = consentSyncActions({
                    prevSectionId: lastSyncedSectionRef.current,
                    currentSectionId,
                    stored: sectionData,
                    knownVersion: draftVersionsRef.current.get(currentSectionId) ?? 0,
                    userChanged: (id) => userChangedRef.current.has(id),
                });
                lastSyncedSectionRef.current = currentSectionId;
                if (sync.flushPrevious) savePreviousSection(sync.flushPrevious, contentRef.current);
                if (sync.setContent !== undefined) setCurrentContent(sync.setContent);
                if (sync.setVersion !== undefined) draftVersionsRef.current.set(currentSectionId, sync.setVersion);
                if (sync.clearMarkFor) userChangedRef.current.delete(sync.clearMarkFor);

                // AUTO-GENERATE: If status is 'pending' and we have no content, trigger AI (but not during Build All)
                if (sectionData.status === 'pending' && !sectionData.content && !loading && !buildingAll) {
                    console.log("Auto-generating section:", currentSectionId);
                    handleGenerate(currentSectionId);
                }
            }
        }
    }, [builderState, currentSectionId]);

    /**
     * Save one section's draft from this page's copy - the ONE draft save
     * path. Sends the version the copy came from; the server refuses the
     * save if the section moved since. Saves of one section run one at a
     * time, so two never claim the same base. Resolves true when saved.
     */
    const saveSectionDraft = (sectionId: string, content: string): Promise<boolean> =>
        enqueueSave(draftQueueRef.current, sectionId, async () => {
            if (staleSectionsRef.current.has(sectionId)) {
                showToast(docChangedMessage('This section'), 'error');
                return false;
            }
            const saveDraft = httpsCallable(functions, CLOUD_FUNCTIONS.SAVE_CONSENT_SECTION_DRAFT);
            try {
                const res = await saveDraft({
                    projectId,
                    artifactId,
                    sectionId,
                    content,
                    baseDraftVersion: draftVersionsRef.current.get(sectionId) ?? 0,
                });
                const written = (res.data as { draftVersion?: unknown } | null)?.draftVersion;
                if (typeof written === 'number' && written > (draftVersionsRef.current.get(sectionId) ?? 0)) {
                    draftVersionsRef.current.set(sectionId, written);
                }
                // Saved. Still marked as changed only if the user typed more
                // into this section while the save was on its way.
                if (currentSectionRef.current !== sectionId || contentRef.current === content) {
                    userChangedRef.current.delete(sectionId);
                }
                return true;
            } catch (err) {
                if (isRefusedAsChanged(err, 'section-changed')) {
                    // Nothing was written. Said plainly; this section is not
                    // saved again from this page until it is reloaded.
                    staleSectionsRef.current.add(sectionId);
                    showToast(docChangedMessage('This section'), 'error');
                    return false;
                }
                throw err;
            }
        });

    const handleSaveDraft = async () => {
        if (!currentSectionId) return;
        setSaveStatus('saving');
        try {
            if (await saveSectionDraft(currentSectionId, currentContent)) {
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 2000);
            } else {
                setSaveStatus('error');
            }
        } catch (err: any) {
            console.error("[ArtifactBuilder] save failed:", err);
            showToast(err.message, 'error');
            setSaveStatus('error');
        }
    };

    // Text that could not be saved after the form moved to another section
    // (another tab, a Redraft) is kept on screen to copy, never dropped.
    const [recoveredText, setRecoveredText] = useState<{ title: string; text: string } | null>(null);

    /**
     * The form moved to another section while the user had unsaved text in
     * `sectionId` (another tab moved it, or a Redraft). That text is saved to
     * the section it belongs to; if the save is refused or fails, it is shown
     * to copy.
     */
    const savePreviousSection = (sectionId: string, content: string) => {
        const decision = decideConsentLeaveSave({
            content,
            userChanged: true,
            stored: builderStateRef.current?.sections?.[sectionId],
            reason: 'leave',
        });
        if (!decision.save) {
            userChangedRef.current.delete(sectionId);
            return;
        }
        const title = currentSectionOrder.find(s => s.id === sectionId)?.title || sectionId;
        const apply = (result: 'saved' | 'refused' | 'error') => {
            const after = afterPreviousSectionSave(result, decision.reopens);
            if (after.reopenedNotice) showToast(REOPENED_NOTICE_TEXT, 'warning');
            if (after.recover) setRecoveredText({ title, text: content });
        };
        saveSectionDraft(sectionId, content).then(
            (saved) => apply(saved ? 'saved' : 'refused'),
            () => apply('error'),
        );
    };

    const handleApprove = async () => {
        if (!currentSectionId) return;
        const sectionId = currentSectionId;
        setLoading(true);
        try {
            const currentIndex = currentSectionOrder.findIndex(s => s.id === sectionId);
            // Approval writes this page's copy as the APPROVED text, so it is
            // checked like a draft save: it sends the version the copy came
            // from, runs in turn with this section's draft saves, and a section
            // already refused as stale is not approved from this page.
            const approved = await enqueueSave(draftQueueRef.current, sectionId, async () => {
                if (staleSectionsRef.current.has(sectionId)) {
                    showToast(docChangedMessage('This section'), 'error');
                    return false;
                }
                const approve = httpsCallable(functions, CLOUD_FUNCTIONS.APPROVE_CONSENT_SECTION, { timeout: CALLABLE_TIMEOUTS.SHORT });
                try {
                    const res = await approve({
                        projectId,
                        artifactId,
                        sectionId,
                        content: currentContent,
                        baseDraftVersion: draftVersionsRef.current.get(sectionId) ?? 0,
                    });
                    const written = (res.data as { draftVersion?: unknown } | null)?.draftVersion;
                    if (typeof written === 'number' && written > (draftVersionsRef.current.get(sectionId) ?? 0)) {
                        draftVersionsRef.current.set(sectionId, written);
                    }
                    return true;
                } catch (err) {
                    if (isRefusedAsChanged(err, 'section-changed')) {
                        // Nothing was written - not the text, not the status.
                        staleSectionsRef.current.add(sectionId);
                        showToast(docChangedMessage('This section'), 'error');
                        return false;
                    }
                    throw err;
                }
            });
            if (!approved) return;
            userChangedRef.current.delete(sectionId);
            // After approval, auto-generate the next section
            const nextSection = currentSectionOrder[currentIndex + 1];
            if (nextSection) {
                const nextState = builderState?.sections?.[nextSection.id];
                if (!nextState || nextState.status === 'pending') {
                    setCurrentContent('');
                    await handleGenerate(nextSection.id);
                    return; // handleGenerate manages loading state
                }
            }
        } catch (err: any) {
            showToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Track current section for async operations
    const currentSectionRef = useRef(currentSectionId);
    useEffect(() => {
        currentSectionRef.current = currentSectionId;
    }, [currentSectionId]);

    const handleGenerate = async (sectionIdToGen?: string) => {
        const targetId = sectionIdToGen || currentSectionId;
        if (!targetId) return;
        setLoading(true);
        const sectionTitle = currentSectionOrder.find(s => s.id === targetId)?.title || targetId;
        startGenProgress(sectionTitle);
        try {
            const generate = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_CONSENT_SECTION, { timeout: CALLABLE_TIMEOUTS.LONG });
            const result: any = await generate({
                projectId,
                artifactId,
                sectionId: targetId
            });

            // Only update editor if we are still on the same section
            if (currentSectionRef.current === targetId) {
                setCurrentContent(result.data.content);
                // The editor now shows the generated text: nothing unsaved.
                userChangedRef.current.delete(targetId);
            }
        } catch (err: any) {
            showToast(err.message, 'error');
        } finally {
            stopGenProgress();
            // Always: if the form moved to another section meanwhile (another
            // tab), the editor must not stay read-only.
            setLoading(false);
        }
    };

    // Build All writes the remaining sections as drafts and never approves
    // anything: a person approves each section (decided 2026-10-01).
    // Sections holding text the user wrote are left as they are. It asks
    // first, and says at the end what it wrote and what it left.
    const [confirmingBuildAll, setConfirmingBuildAll] = useState<{
        plan: BuildAllStep[];
        question: string;
    } | null>(null);

    const handleBuildAll = async () => {
        // The section on screen is saved first, so its text counts as the user's.
        const outcome = await flushCurrentSection('leave');
        if (!navigationAllowedAfterFlush(outcome)) {
            showToast(outcome === 'refused' ? LEAVE_BLOCKED_REFUSED_TEXT : LEAVE_BLOCKED_ERROR_TEXT, 'error');
            return;
        }
        const sections: Record<string, any> = { ...(builderStateRef.current?.sections ?? {}) };
        const savedId = currentSectionRef.current;
        if (outcome === 'saved' && savedId) {
            // The snapshot with this save may not have arrived yet.
            sections[savedId] = { ...sections[savedId], content: contentRef.current, userEdited: true, status: 'draft' };
        }
        const plan = buildAllPlan(currentSectionOrder, sections);
        const toWrite = plan.filter((step) => step.action === 'generate').length;
        const skipped = plan.filter((step) => step.action === 'skip').map((step) => step.title);
        if (toWrite === 0) {
            showToast(buildAllSummary({ ended: 'finished', written: [], left: skipped }), 'info');
            return;
        }
        setConfirmingBuildAll({ plan, question: buildAllQuestion(toWrite, skipped) });
    };

    const runBuildAll = async (plan: BuildAllStep[]) => {
        buildAllCancelledRef.current = false;
        const toWrite = plan.filter((step) => step.action === 'generate');
        const written: string[] = [];
        const left: string[] = plan.filter((step) => step.action === 'skip').map((step) => step.title);
        setBuildingAll(true);
        setBuildAllProgress({ current: 0, total: toWrite.length, currentSection: '' });

        const generate = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_CONSENT_SECTION, { timeout: CALLABLE_TIMEOUTS.LONG });

        let ended: 'finished' | 'cancelled' | 'failed' = 'finished';
        let failedTitle: string | undefined;
        let failedReason = '';
        for (let i = 0; i < toWrite.length; i++) {
            const step = toWrite[i];
            if (buildAllCancelledRef.current) {
                ended = 'cancelled';
                left.push(...toWrite.slice(i).map((s) => s.title));
                break;
            }
            setBuildAllProgress({ current: i + 1, total: toWrite.length, currentSection: step.title });
            try {
                // The planned base: the server refuses to write over text the
                // user saved since the plan (another tab), and nothing is written.
                await generate({ projectId, artifactId, sectionId: step.id, baseDraftVersion: step.baseDraftVersion });
                written.push(step.title);
            } catch (err: any) {
                if (isRefusedAsChanged(err, 'section-changed')) {
                    left.push(step.title);
                    continue;
                }
                console.error(`Build All failed on section ${step.id}:`, err);
                ended = 'failed';
                failedTitle = step.title;
                failedReason = serverFailureReason(err);
                left.push(...toWrite.slice(i).map((s) => s.title));
                break;
            }
        }

        setBuildingAll(false);
        showToast(buildAllSummary({ ended, failedTitle, reason: failedReason, written, left }), ended === 'failed' ? 'error' : 'info');
    };

    // "Approve all drafts" approves each draft through the same callable as
    // the per-section approval, with its own base, so a section changed
    // elsewhere is refused and named. It asks once first.
    const [confirmingApproveAll, setConfirmingApproveAll] = useState<ApprovableDraft[] | null>(null);

    const runApproveAll = async (drafts: ApprovableDraft[]) => {
        const outcome = await flushCurrentSection('leave');
        if (!navigationAllowedAfterFlush(outcome)) {
            showToast(outcome === 'refused' ? LEAVE_BLOCKED_REFUSED_TEXT : LEAVE_BLOCKED_ERROR_TEXT, 'error');
            return;
        }
        setLoading(true);
        const approve = httpsCallable(functions, CLOUD_FUNCTIONS.APPROVE_CONSENT_SECTION, { timeout: CALLABLE_TIMEOUTS.SHORT });
        const approved: string[] = [];
        const refused: string[] = [];
        try {
            for (const d of drafts) {
                try {
                    const res = await approve({ projectId, artifactId, sectionId: d.id, content: d.content, baseDraftVersion: d.baseDraftVersion });
                    const written = (res.data as { draftVersion?: unknown } | null)?.draftVersion;
                    if (typeof written === 'number' && written > (draftVersionsRef.current.get(d.id) ?? 0)) {
                        draftVersionsRef.current.set(d.id, written);
                    }
                    userChangedRef.current.delete(d.id);
                    approved.push(d.title);
                } catch (err) {
                    if (isRefusedAsChanged(err, 'section-changed')) {
                        refused.push(d.title);
                        continue;
                    }
                    throw err;
                }
            }
            showToast(approveAllSummary(approved, refused), refused.length ? 'warning' : 'success');
        } catch (err: any) {
            showToast(`${approveAllSummary(approved, refused)} ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerateClick = () => {
        const section = currentSectionId ? builderState?.sections?.[currentSectionId] : null;
        if (regenerateNeedsConfirm(section)) {
            setConfirmingRegenerate(true);
            return;
        }
        handleGenerate();
    };

    const handleCancelBuildAll = () => {
        buildAllCancelledRef.current = true;
        setBuildingAll(false);
    };

    /**
     * Save the section being left, when the user changed its text
     * (decideConsentLeaveSave). On a real leave an approved section the user
     * edited is saved too: the save sets it back to draft, and the page says
     * so, rather than dropping the edit. A tab merely hidden saves draft
     * sections only. Says whether the save landed, was refused (changed
     * elsewhere) or failed, so the caller can stop instead of losing text.
     */
    const flushCurrentSection = async (reason: 'leave' | 'hidden'): Promise<FlushOutcome> => {
        const sectionId = currentSectionRef.current;
        if (!sectionId) return 'none';
        const content = contentRef.current;
        const decision = decideConsentLeaveSave({
            content,
            userChanged: userChangedRef.current.has(sectionId),
            stored: builderStateRef.current?.sections?.[sectionId],
            reason,
        });
        if (!decision.save) return 'none';
        try {
            const saved = await saveSectionDraft(sectionId, content);
            if (saved && decision.reopens) showToast(REOPENED_NOTICE_TEXT, 'warning');
            return saved ? 'saved' : 'refused';
        } catch (e) {
            console.error('[ArtifactBuilder] save failed:', e);
            return 'error';
        }
    };

    // Leaving the page or closing the builder saves the user's edit too.
    // Through a ref, so the handlers always see the latest text.
    const flushRef = useRef<(reason: 'leave' | 'hidden') => void>(() => undefined);
    flushRef.current = (reason) => {
        flushCurrentSection(reason).catch((e) => console.error('[ArtifactBuilder] save on leave failed:', e));
    };
    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            const sectionId = currentSectionRef.current;
            if (!sectionId || !userChangedRef.current.has(sectionId)) return;
            flushRef.current('leave');
            // The save may not finish before the page goes, so the browser asks.
            e.preventDefault();
            e.returnValue = '';
        };
        // Switching tabs saves draft sections only, never un-approving one.
        const onVisibility = () => { if (document.visibilityState === 'hidden') flushRef.current('hidden'); };
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('visibilitychange', onVisibility);
            flushRef.current('leave');
        };
    }, []);

    // Moving on is stopped when the user's change was refused or not saved,
    // and the page offers: keep editing, or discard the change.
    const [blockedLeave, setBlockedLeave] = useState<{ targetIndex: number; outcome: FlushOutcome; close?: boolean } | null>(null);
    useEffect(() => { setBlockedLeave(null); }, [currentSectionId]);

    // Safe Navigation that autosaves
    const handleNavigation = async (targetIndex: number) => {
        if (targetIndex === builderState.currentSectionIndex) return;

        // Save what the user typed into the section being left, approved or
        // not. Awaited: the server may set the form up again for new study
        // countries when it moves to the next section, and it decides that
        // from whether any section has text. The draft must land first.
        const outcome = await flushCurrentSection('leave');
        if (!navigationAllowedAfterFlush(outcome)) {
            // Nothing is lost: the page stays here, with the user's text.
            setSaveStatus('error');
            setBlockedLeave({ targetIndex, outcome });
            return;
        }

        const targetSection = currentSectionOrder[targetIndex];
        setLoading(true);
        try {
            const navFn = httpsCallable(functions, CLOUD_FUNCTIONS.NAVIGATE_TO_SECTION);
            await navFn({ projectId, artifactId, sectionId: targetSection.id });
        } catch (e: any) {
            showToast(e.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Close waits for the save of the section on screen, and stops with the
    // same choice when it is refused or fails, so closing never loses text.
    const handleClose = async () => {
        const outcome = await flushCurrentSection('leave');
        if (!navigationAllowedAfterFlush(outcome)) {
            setSaveStatus('error');
            setBlockedLeave({ targetIndex: -1, outcome, close: true });
            return;
        }
        onClose();
    };

    // The user chose to drop their change: load the saved text, then move on
    // (or close, when it was Close that was stopped).
    const discardChangeAndLeave = () => {
        const blocked = blockedLeave;
        const sectionId = currentSectionRef.current;
        setBlockedLeave(null);
        if (!blocked || !sectionId) return;
        const stored = builderStateRef.current?.sections?.[sectionId];
        userChangedRef.current.delete(sectionId);
        staleSectionsRef.current.delete(sectionId);
        setCurrentContent(typeof stored?.content === 'string' ? stored.content : '');
        const v = stored?.draftVersion;
        if (typeof v === 'number' && v > (draftVersionsRef.current.get(sectionId) ?? 0)) {
            draftVersionsRef.current.set(sectionId, v);
        }
        if (blocked.close) {
            onClose();
            return;
        }
        const target = blocked.targetIndex;
        void handleNavigation(target);
    };

    if (!builderState) {
        return (
            <div className="flex items-center justify-center flex-col min-h-[60vh]">
                <div className="text-center p-8 max-w-md">
                    <h2 className="text-xl font-bold mb-4">Initialize Form Builder</h2>
                    <p className="text-gray-600 mb-6">This artifact is in draft mode but hasn't been set up for the section builder yet.</p>
                    <button
                        onClick={async () => {
                            setLoading(true);
                            try {
                                const initFn = httpsCallable(functions, CLOUD_FUNCTIONS.INITIALIZE_CONSENT_BUILDER, { timeout: CALLABLE_TIMEOUTS.SHORT });
                                await initFn({ projectId, artifactId });
                                // Snapshot will pick up the change
                            } catch (error: any) {
                                showToast(error.message, 'error');
                            } finally {
                                setLoading(false);
                            }
                        }}
                        disabled={loading}
                        className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
                    >
                        {loading ? "Initializing..." : "Start Builder Mode"}
                    </button>
                    <button onClick={() => { void handleClose(); }} className="mt-4 text-gray-500 hover:text-gray-700 block mx-auto text-sm">Cancel</button>
                </div>
            </div>
        );
    }

    // Calculate Progress
    const completedSections = Object.values(builderState.sections).filter((s: any) => s.status === 'approved').length;
    const progressPercent = Math.round((completedSections / currentSectionOrder.length) * 100);
    const isComplete = builderState.isComplete || completedSections === currentSectionOrder.length;
    // The drafts "Approve all drafts" would approve; empty hides the button.
    const approvable = approveAllDrafts(currentSectionOrder, builderState.sections);

    // Show completion screen if all sections are approved
    if (isComplete) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center p-8 max-w-2xl">
                    <div className="mb-6">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="h-12 w-12 text-green-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">{artifactType === 'case-report-form' ? 'CRF Package Complete! 🎉' : 'Consent Form Complete! 🎉'}</h2>
                        <p className="text-gray-600 text-lg">
                            All {currentSectionOrder.length} sections have been approved and consolidated into a final document.
                        </p>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-6 mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
                        <ul className="text-left text-gray-700 space-y-2">
                            <li className="flex items-start">
                                <span className="text-green-600 mr-2">✓</span>
                                <span className="text-slate-700">The document has been saved with status "Generated"</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-600 mr-2">✓</span>
                                <span className="text-slate-700">You can view it from the project artifacts list</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-600 mr-2">✓</span>
                                <span className="text-slate-700">You can export it to Word or PDF for final review</span>
                            </li>
                        </ul>
                    </div>

                    <div className="flex justify-center gap-4">
                        <button
                            onClick={() => { void handleClose(); }}
                            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                        >
                            Close Builder
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
        {privacyNotice}
        <div className="flex h-[calc(100vh-16rem)] min-h-[500px] bg-white rounded-lg border border-slate-200 overflow-hidden">
            {/* Sidebar */}
            <div className="w-64 bg-slate-50 border-r border-slate-200 flex flex-col">
                <div className="p-4 border-b border-slate-200">
                    <h2 className="font-bold text-slate-800">Form Builder</h2>
                    <div className="mt-2">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>Progress</span>
                            <span>{progressPercent}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-1.5">
                            <div className="bg-green-500 h-1.5 rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }} />
                        </div>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                    {currentSectionOrder.map((section, idx) => {
                        const state = builderState.sections[section.id];
                        const isActive = idx === builderState.currentSectionIndex;
                        let statusColor = "bg-slate-200";
                        if (state?.status === 'approved') statusColor = "bg-green-500";
                        if (isActive) statusColor = "bg-blue-500";



                        return (
                            <button
                                key={section.id}
                                onClick={() => handleNavigation(idx)}
                                className={`w-full flex items-center p-2 rounded-lg mb-1 text-left transition-colors ${isActive ? 'bg-white shadow-sm ring-1 ring-slate-200' :
                                    'hover:bg-slate-100 cursor-pointer text-slate-600'
                                    }`}
                            >
                                <div className={`w-2 h-2 rounded-full mr-3 flex-shrink-0 ${statusColor}`} />
                                <span className={`text-sm font-medium truncate ${isActive ? 'text-slate-900' : 'text-slate-600'}`}>
                                    {section.title}
                                </span>
                                {state?.status === 'approved' && <span className="ml-auto text-green-600 font-bold">✓</span>}
                            </button>

                        );
                    })}
                </div>
            </div>

            {/* Main Area */}
            <div className="flex-1 flex flex-col h-full">
                <div className="p-4 bg-white border-b border-slate-200 flex justify-between items-center shadow-sm z-10">
                    <h3 className="text-lg font-bold text-slate-800">
                        {currentSectionOrder[builderState.currentSectionIndex]?.title}
                    </h3>
                    <div className="flex space-x-2 items-center">
                        {/* Save Status Indicator */}
                        {saveStatus === 'saved' && <span className="text-xs text-green-600 font-medium mr-2 animate-pulse">Draft Saved!</span>}
                        {saveStatus === 'saving' && <span className="text-xs text-slate-500 font-medium mr-2">Saving...</span>}

                        <button onClick={handleRegenerateClick} disabled={loading || buildingAll} className="px-3 py-2 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded font-bold" title="Regenerate">
                            {loading ? <span>...</span> : <span className="flex items-center">✨</span>}
                        </button>
                        <button
                            onClick={handleBuildAll}
                            disabled={loading || buildingAll}
                            className="px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded flex items-center gap-1.5 transition-colors border border-purple-200"
                            title="Write all remaining sections as drafts for you to approve"
                        >
                            <Zap className="w-4 h-4" />
                            Build All
                        </button>
                        {approvable.length > 0 && !buildingAll && (
                            <button
                                onClick={() => setConfirmingApproveAll(approvable)}
                                disabled={loading}
                                className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200"
                            >
                                Approve all drafts
                            </button>
                        )}
                        <button onClick={() => { void handleClose(); }} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">
                            Close
                        </button>
                        <button
                            onClick={handleSaveDraft}
                            disabled={loading || saveStatus === 'saving' || buildingAll}
                            className="px-3 py-1.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded flex items-center transition-colors">
                            <FileText className="w-4 h-4 mr-1.5" />
                            Save Draft
                        </button>
                        <button
                            onClick={handleApprove}
                            disabled={loading || buildingAll}
                            className="px-4 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded shadow-sm flex items-center">
                            Approve and continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 p-6 bg-slate-50 overflow-y-auto">
                    {/* An approved section given new text is back to draft. */}
                    {currentSectionId && reopenedNoticeNeeded(builderState?.sections?.[currentSectionId]) && (
                        <div role="status" className="max-w-3xl mx-auto mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            {REOPENED_NOTICE_TEXT}
                        </div>
                    )}
                    {blockedLeave && (
                        <div role="alertdialog" className="max-w-3xl mx-auto mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                            <p>{blockedLeave.outcome === 'refused' ? LEAVE_BLOCKED_REFUSED_TEXT : LEAVE_BLOCKED_ERROR_TEXT}</p>
                            <div className="mt-3 flex justify-end gap-2">
                                <button
                                    onClick={() => setBlockedLeave(null)}
                                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                                >
                                    Keep editing
                                </button>
                                <button
                                    onClick={discardChangeAndLeave}
                                    className="rounded-lg bg-red-600 px-3 py-1.5 font-medium text-white hover:bg-red-700"
                                >
                                    Discard my change
                                </button>
                            </div>
                        </div>
                    )}
                    {recoveredText && (
                        <div role="status" className="max-w-3xl mx-auto mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            <p>{MOVED_AWAY_UNSAVED_TEXT}</p>
                            <p className="mt-2 font-medium">{recoveredText.title}</p>
                            <textarea readOnly value={recoveredText.text} className="mt-2 h-32 w-full rounded border border-amber-200 bg-white p-2 font-serif" />
                            <div className="mt-2 flex justify-end">
                                <button
                                    onClick={() => setRecoveredText(null)}
                                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    )}
                    {confirmingBuildAll && (
                        <div role="alertdialog" className="max-w-3xl mx-auto mb-4 flex items-center justify-between gap-4 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-900">
                            <span>{confirmingBuildAll.question}</span>
                            <div className="flex shrink-0 gap-2">
                                <button
                                    onClick={() => setConfirmingBuildAll(null)}
                                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => { const c = confirmingBuildAll; setConfirmingBuildAll(null); void runBuildAll(c.plan); }}
                                    className="rounded-lg bg-purple-600 px-3 py-1.5 font-medium text-white hover:bg-purple-700"
                                >
                                    Build All
                                </button>
                            </div>
                        </div>
                    )}
                    {confirmingApproveAll && (
                        <div role="alertdialog" className="max-w-3xl mx-auto mb-4 flex items-center justify-between gap-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
                            <span>{approveAllQuestion(confirmingApproveAll.length)}</span>
                            <div className="flex shrink-0 gap-2">
                                <button
                                    onClick={() => setConfirmingApproveAll(null)}
                                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => { const c = confirmingApproveAll; setConfirmingApproveAll(null); void runApproveAll(c); }}
                                    className="rounded-lg bg-green-600 px-3 py-1.5 font-medium text-white hover:bg-green-700"
                                >
                                    Approve all
                                </button>
                            </div>
                        </div>
                    )}
                    {confirmingRegenerate && (
                        <div role="alertdialog" className="max-w-3xl mx-auto mb-4 flex items-center justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            <span>{REGENERATE_APPROVED_CONFIRM_TEXT}</span>
                            <div className="flex shrink-0 gap-2">
                                <button
                                    onClick={() => setConfirmingRegenerate(false)}
                                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => { setConfirmingRegenerate(false); handleGenerate(); }}
                                    className="rounded-lg bg-amber-600 px-3 py-1.5 font-medium text-white hover:bg-amber-700"
                                >
                                    Regenerate
                                </button>
                            </div>
                        </div>
                    )}
                    {/* Build All Progress Overlay */}
                    {buildingAll && (
                        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg mb-6 p-8">
                            <div className="flex flex-col items-center space-y-5">
                                <div className="relative">
                                    <div className="w-20 h-20 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Zap className="w-8 h-8 text-purple-600" />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-xl font-bold text-slate-800">Building All Sections</h3>
                                    <p className="text-sm text-slate-500 mt-1">
                                        Section {buildAllProgress.current} of {buildAllProgress.total}: <strong>{buildAllProgress.currentSection}</strong>
                                    </p>
                                </div>
                                <div className="w-full max-w-md">
                                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                                        <span>Progress</span>
                                        <span>{Math.round((buildAllProgress.current / Math.max(buildAllProgress.total, 1)) * 100)}%</span>
                                    </div>
                                    <div className="w-full bg-slate-200 rounded-full h-3">
                                        <div
                                            className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-700"
                                            style={{ width: `${(buildAllProgress.current / Math.max(buildAllProgress.total, 1)) * 100}%` }}
                                        />
                                    </div>
                                </div>
                                {/* Show which sections are done */}
                                <div className="w-full max-w-md space-y-1">
                                    {currentSectionOrder.map((section) => {
                                        const state = builderState?.sections?.[section.id];
                                        const isApproved = state?.status === 'approved';
                                        const isCurrent = section.title === buildAllProgress.currentSection;
                                        return (
                                            <div key={section.id} className={`flex items-center text-sm px-3 py-1 rounded ${isCurrent ? 'bg-purple-50 text-purple-700 font-medium' : isApproved ? 'text-green-600' : 'text-slate-400'}`}>
                                                {isApproved ? (
                                                    <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                                                ) : isCurrent ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin text-purple-600" />
                                                ) : (
                                                    <div className="w-4 h-4 mr-2 rounded-full border border-slate-300" />
                                                )}
                                                {section.title}
                                            </div>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={handleCancelBuildAll}
                                    className="px-4 py-2 text-sm text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg min-h-[500px] flex flex-col relative">
                        {loading && !currentContent ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white rounded-xl z-10">
                                <div className="flex flex-col items-center space-y-4 w-72">
                                    <div className="relative">
                                        <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-2xl">✨</span>
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-lg font-medium text-slate-700">
                                            {genProgress.message || `Generating ${currentSectionOrder[builderState.currentSectionIndex]?.title}...`}
                                        </p>
                                    </div>
                                    <div className="w-full">
                                        <div className="flex justify-between text-xs text-slate-400 mb-1">
                                            <span>Progress</span>
                                            <span>{genProgress.percent || 5}%</span>
                                        </div>
                                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-blue-500 to-primary-600 rounded-full transition-all duration-1000 ease-out"
                                                style={{ width: `${genProgress.percent || 5}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        <textarea
                            value={currentContent}
                            readOnly={loading || buildingAll || confirmingBuildAll !== null}
                            onChange={(e) => {
                                if (currentSectionId) userChangedRef.current.add(currentSectionId);
                                setCurrentContent(e.target.value);
                                if (saveStatus === 'saved') setSaveStatus('idle'); // Reset saved state on edit
                            }}
                            className="flex-1 w-full p-8 font-serif text-lg leading-relaxed outline-none resize-none rounded-xl"
                            placeholder="Content will be generated automatically, or start typing..."
                        />
                    </div>
                </div>
            </div>
        </div >
        </div>
    );
}
